import { create } from 'zustand';
import api from '../api/axios';

/**
 * Cache data master (kategori & dompet) dengan pola
 * stale-while-revalidate:
 *
 * 1. Bila cache masih segar (di bawah TTL) -> data langsung tersedia,
 *    TIDAK ada request ke server.
 * 2. Bila cache ada tapi sudah basi -> data basi tetap ditampilkan
 *    (UI instan), sambil fetch ulang di background. Begitu selesai,
 *    komponen yang subscribe otomatis ikut data terbaru.
 * 3. Bila belum ada cache -> fetch, komponen menampilkan kosong dulu.
 *
 * Cache per book_id, sehingga berpindah buku tidak saling tertukar.
 * Dedupe in-flight mencegah dobel request (StrictMode memanggil
 * useEffect 2x di mode dev).
 *
 * Karena kategori/dompet juga bisa diubah dari perangkat member lain,
 * angka TTL sengaja pendek (5 menit) sebagai kompromi kesegaran.
 * Setelah mutasi lokal (add/save/delete), panggil invalidate*() atau
 * load*({force:true}) agar cache langsung diperbarui.
 */

const TTL = 5 * 60 * 1000; // 5 menit

export const useMasterStore = create((set, get) => ({
    categories: {}, // book_id -> { data: {debit:[], kredit:[]}, fetchedAt }
    pockets: {},    // book_id -> { data: [], fetchedAt }
    inflight: {},   // dedupe: 'cat|id' / 'poc|id' -> Promise

    loadCategories: (book_id, { force = false } = {}) => {
        const entry = get().categories[book_id];
        if (!entry) {
            // belum ada cache -> tunggu fetch
            return get().fetchCategories(book_id);
        }
        const fresh = Date.now() - entry.fetchedAt <= TTL;
        if (force || fresh) {
            return get().fetchCategories(book_id);
        }
        // stale: data lama langsung dipakai, sambil fetch ulang di background
        get().fetchCategories(book_id);
        return Promise.resolve(entry.data);
    },

    loadPockets: (book_id, { force = false } = {}) => {
        const entry = get().pockets[book_id];
        if (!entry) {
            return get().fetchPockets(book_id);
        }
        const fresh = Date.now() - entry.fetchedAt <= TTL;
        if (force || fresh) {
            return get().fetchPockets(book_id);
        }
        get().fetchPockets(book_id);
        return Promise.resolve(entry.data);
    },

    fetchCategories: (book_id) => {
        const key = 'cat|' + book_id;

        // sudah ada fetch berjalan -> pakai janji yang sama (dedupe)
        if (get().inflight[key]) {
            return get().inflight[key];
        }

        const promise = (async () => {
            try {
                const res = await api.post('/categories', { book_id: book_id });
                const data = {
                    debit: res.data.categories.debit || [],
                    kredit: res.data.categories.kredit || []
                };
                set((s) => ({
                    categories: {
                        ...s.categories,
                        [book_id]: { data: data, fetchedAt: Date.now() }
                    }
                }));
                return data;
            } catch (error) {
                console.error('Gagal memuat kategori:', error?.response || error?.message);
                return get().categories[book_id]?.data || null;
            } finally {
                set((s) => {
                    const inflight = { ...s.inflight };
                    delete inflight[key];
                    return { inflight: inflight };
                });
            }
        })();

        set((s) => ({ inflight: { ...s.inflight, [key]: promise } }));
        return promise;
    },

    fetchPockets: (book_id) => {
        const key = 'poc|' + book_id;

        if (get().inflight[key]) {
            return get().inflight[key];
        }

        const promise = (async () => {
            try {
                const res = await api.post('/pockets', { book_id: book_id });
                const data = res.data.pockets || [];
                set((s) => ({
                    pockets: {
                        ...s.pockets,
                        [book_id]: { data: data, fetchedAt: Date.now() }
                    }
                }));
                return data;
            } catch (error) {
                console.error('Gagal memuat dompet:', error?.response || error?.message);
                return get().pockets[book_id]?.data || null;
            } finally {
                set((s) => {
                    const inflight = { ...s.inflight };
                    delete inflight[key];
                    return { inflight: inflight };
                });
            }
        })();

        set((s) => ({ inflight: { ...s.inflight, [key]: promise } }));
        return promise;
    },

    invalidateCategories: (book_id) => {
        set((s) => {
            const categories = { ...s.categories };
            delete categories[book_id];
            return { categories: categories };
        });
    },

    invalidatePockets: (book_id) => {
        set((s) => {
            const pockets = { ...s.pockets };
            delete pockets[book_id];
            return { pockets: pockets };
        });
    }
}));
